/**
 * PRR i18n pt-PT - Reescreve os textos da interface para português europeu.
 *
 * Os utilizadores do PRR são portugueses, mas os locales do core estão em
 * pt-BR ("Salvar", "Deletar", "arquivo", "Carregando...", "usuário").
 *
 * Em vez de editar `src/locales/pt-BR/*.json` (core, e fonte garantida de
 * conflitos nos merges com a develop), registamos aqui apenas as chaves cujo
 * texto difere e sobrepomo-las ao bundle pt-BR no arranque.
 *
 * Chaves não listadas mantêm o texto original do core.
 */

type NamespaceOverrides = Record<string, unknown>;

export const PRR_PT_PT_OVERRIDES: Record<string, NamespaceOverrides> = {
  common: {
    loading: "A carregar...",
    save: "Guardar",
    delete: "Eliminar",
    search: "Pesquisar",
    user: "Utilizador",
    deleting: "A eliminar...",
    saving: "A guardar...",
    adding: "A adicionar...",
    updating: "A atualizar...",
    download: "Transferir",
    verifyingAuth: "A verificar a autenticação...",
    verifyingAuthentication: "A verificar a autenticação...",
    record: "registo",
    records: "registos",
    exporting: "A exportar...",
    redirecting: "A redirecionar...",
  },

  chat: {
    placeholder: "Escreva a sua pergunta...",
    suggestions: {
      valueAnalysisDesc: "Mostre os 10 principais registos por valor",
      recordCount: "Contagem de registos",
      recordCountDesc: "Quantos registos existem na tabela?",
    },
    executionTimeline: "Cronologia",
    thinking: "A pensar...",
    generatingResponse: "A gerar a resposta...",
    toolExecution: "A executar a ferramenta",
    chatWith: "Converse com os seus dados",
    askDataQuestion: "Faça uma pergunta sobre os seus dados...",
    uploadFileFirst: "Envie primeiro um ficheiro Excel",
    clearChatDescription:
      "Esta ação irá remover todas as mensagens desta conversa. A sessão permanecerá ativa e as memórias serão preservadas. Tem a certeza de que pretende continuar?",
    clearing: "A limpar...",
    deleteMessage: "Eliminar mensagem",
    noActiveFile: "Nenhum ficheiro ativo",
    noActiveFileDesc: "Por favor, envie um ficheiro Excel antes de fazer consultas.",
    queryProcessedDesc: "A sua consulta foi processada com sucesso.",
    messageDeleted: "Mensagem eliminada",
    deleteMessageError: "Erro ao eliminar mensagem",
    deleteMessageErrorDesc: "Não foi possível eliminar a mensagem.",
    noDeletePermission: "Não tem permissão para eliminar esta mensagem.",
    authRequiredDelete: "Tem de estar autenticado para eliminar mensagens.",
    tagline: "Faça perguntas sobre os seus dados em linguagem natural",
    feedback: {
      shareTitle: "Partilhe o seu Feedback",
      shareDesc: "O seu feedback será guardado na memória do sistema",
      yourQuestion: "A sua pergunta:",
      feedbackAbout: "Sobre o que é o seu feedback?",
      yourFeedback: "O seu feedback:",
      sharePlaceholder:
        "Partilhe a sua opinião: o que funcionou bem, o que poderia melhorar, sugestões...",
      usedToImprove: "O seu feedback será usado para melhorar o sistema.",
      submitting: "A enviar...",
      responseApproved: "Resposta aprovada pelo utilizador",
      sqlApproved: "Consultas SQL aprovadas pelo utilizador",
      positiveRecorded: "Feedback positivo registado",
      positiveRecordedDesc: "Obrigado! A resposta foi guardada como exemplo positivo.",
      recordError: "Erro ao registar feedback",
      negativeRecorded: "Feedback negativo registado",
      feedbackReset: "Feedback reposto",
      feedbackResetDesc:
        "As memórias foram removidas da base de dados e pode dar um novo feedback.",
      resetError: "Erro ao repor feedback",
    },
    execution: {
      thinking: "A pensar...",
    },
    feedbackSection: {
      processing: "A processar feedback...",
    },
  },

  session: {
    deleteSuccess: "Sessão eliminada com sucesso",
    deleteError: "Erro ao eliminar sessão",
    enableRag: "Ativar RAG",
    disableRag: "Desativar RAG",
    ragEnabled: "RAG ativado",
    ragDisabled: "RAG desativado",
    deleteConfirm: "Tem a certeza de que pretende eliminar esta sessão?",
    deleteSession: "Eliminar Sessão",
    deleteConfirmDesc:
      'Esta ação não pode ser desfeita. Irá eliminar permanentemente a sessão "{{name}}" e as suas mensagens.',
    deleteErrorDesc: "Não foi possível eliminar a sessão.",
    loadingSessions: "A carregar sessões...",
    noSessionsDesc:
      "Crie uma nova sessão para começar a analisar os seus dados e conversar com o agente.",
    loadingInstructions: "A carregar instruções...",
    deleteInstructionsError: "Erro ao eliminar instruções",
    deleteInstructionsErrorDesc: "Não foi possível eliminar as instruções.",
    instructionsSavedLocal: "Instruções guardadas",
    instructionsSavedLocalDesc: "As instruções foram guardadas com sucesso.",
    saveInstructionsError: "Erro ao guardar instruções",
    saveInstructionsErrorDesc: "Não foi possível guardar as instruções.",
    loadingSessionData: "A carregar sessão...",
    customInstructionsDesc:
      "Crie instruções específicas para ensinar o agente sobre o contexto dos seus dados, como calcular indicadores e regras de negócio específicas.",
    tipDataContext:
      "Contexto dos Dados: Descreva o que representam as suas tabelas e colunas",
    tipFormatting: "Formatação: Indique como apresentar valores (moeda, percentagem, etc.)",
  },

  sidebar: {
    logoutDescription: "Sessão terminada com sucesso",
    user: "Utilizador",
  },

  shared: {
    tabs: {
      files: "Ficheiros",
    },
    streaming: {
      agentProcessing: "Agente a processar",
    },
    upload: {
      waiting: "A aguardar...",
      sending: "A enviar...",
      processing: "A processar...",
      annotating: "A anotar...",
    },
    processing: {
      starting: "A iniciar o processamento...",
    },
    uploadContext: {
      starting: "A iniciar o carregamento...",
    },
    errorBoundary: {
      somethingWentWrong: "Algo correu mal",
      unexpectedError:
        "Ocorreu um erro inesperado. Tente recarregar a página ou volte à página inicial.",
    },
    loader: {
      loading: "A carregar...",
    },
    auth: {
      loading: "A carregar...",
      verifyingAuth: "A verificar a autenticação...",
    },
  },

  errors: {
    somethingWentWrong: "Algo correu mal",
    fileProcessingError: "Erro ao processar o ficheiro",
    connectionError: "Erro de ligação",
    databaseError: "Erro de base de dados",
    pageNotFoundDesc: "Ups! Página não encontrada",
    returnToHome: "Voltar ao início",
  },

  auth: {
    register: "Registar",
    password: "Palavra-passe",
    forgotPassword: "Esqueceu-se da palavra-passe?",
    sessionExpired: "Sessão expirada. Inicie sessão novamente.",
    sessionExpiredRenew:
      "Não foi possível renovar a sessão. Por favor, inicie sessão novamente.",
    accessDeniedDesc: "Não tem permissão para aceder a este sistema.",
    returnToLogin: "Voltar ao início de sessão",
    authenticating: "A autenticar...",
    loginDescription: "Inicie sessão com a sua conta para aceder à plataforma",
    enterDetails: "Preencha os seus dados para criar a sua conta",
    loginError: "Erro ao iniciar sessão",
    authServerError: "Não foi possível ligar ao servidor de autenticação.",
    callback: {
      processing: "A processar a autenticação...",
      validating: "A validar as credenciais com o servidor de autenticação.",
    },
  },

  memory: {
    title: "Gestor de Memória",
    questionPlaceholder: "Escreva a pergunta do utilizador",
    answerPlaceholder: "Escreva a resposta do agente",
    sqlPlaceholder: "Escreva a consulta SQL gerada",
    memoryDeleted: "Memória eliminada",
    memoryDeletedDesc: "A memória foi eliminada com sucesso.",
    deleteError: "Erro ao eliminar memória",
    deleteErrorDesc: "Não foi possível eliminar a memória.",
    deleteErrorGeneric: "Ocorreu um erro ao eliminar a memória.",
    allDeleted: "Memórias eliminadas",
    allDeletedDesc: "Todas as memórias da sessão foram eliminadas com sucesso.",
    deleteAllError: "Erro ao eliminar memórias",
    deleteAllErrorDesc: "Não foi possível eliminar as memórias da sessão.",
    deleteAllErrorGeneric: "Ocorreu um erro ao eliminar as memórias da sessão.",
    removeAllDesc:
      "Esta ação não pode ser desfeita. Irá eliminar permanentemente todas as memórias desta sessão.",
    deleteAll: "Eliminar Tudo",
    removeMemoryDesc:
      "Tem a certeza de que pretende remover esta memória? Esta ação não pode ser desfeita.",
    removing: "A remover...",
    loadingMemories: "A carregar memórias...",
    noSearchResults: "Nenhum resultado encontrado para a sua pesquisa.",
    clearSearch: "Limpar pesquisa",
    searchPlaceholder: "Pesquisar nas memórias...",
    sessionMemoriesDesc: "Faça a gestão das memórias usadas pelo agente e pelo Text2SQL",
    feedbackSaveError: "Erro ao guardar feedback",
    feedbackSaveErrorDesc: "Ocorreu um erro ao guardar o feedback.",
    saveFeedback: "Guardar Feedback",
  },

  admin: {
    modelConfigDescription: "Faça a gestão das configurações de modelos LLM do sistema",
    userManagement: "Gestão de Utilizadores",
    userManagementDescription: "Faça a gestão dos roles dos utilizadores do sistema",
    deleteConfig: "Eliminar Configuração",
    deleteError: "Erro ao eliminar configuração",
    deleteErrorDescription: "Não foi possível eliminar a configuração.",
    deleteSuccess: "Configuração eliminada",
    deleteSuccessDescription: "A configuração foi eliminada com sucesso.",
    deleteConfirm: "Tem a certeza de que pretende eliminar esta configuração?",
    deleteConfirmMessage:
      "Tem a certeza de que pretende eliminar a configuração {{configKey}}? Esta ação não pode ser desfeita.",
    noUsers: "Nenhum utilizador encontrado.",
    changeUserRole: "Alterar Role do Utilizador",
    cannotRemoveOwnAdmin: "Não pode remover o seu próprio role de administrador.",
    roleUpdateErrorDescription: "Não foi possível atualizar o role do utilizador.",
    confirmRoleChangeDescription:
      "Tem a certeza de que pretende alterar o role de {{userName}} de {{currentRole}} para {{newRole}}?",
    adminPageDesc: "Faça a gestão das configurações de modelos e dos utilizadores do sistema",
    checkingPermissions: "A verificar permissões...",
    onlyAdminsAccess: "Apenas administradores podem aceder a esta página.",
    userManagementTab: "Gestão de Utilizadores",
  },
};
